// Home: the platform team's "what needs me now" view (design direction A).
// Needs you first, then Discovery Sessions this week, every active firm's progress, and recent firm activity.
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, CalendarClock, Check, CircleCheck, ClipboardCheck, FileClock, FileWarning, Hourglass, Link2, ListTodo, MessageSquare, Plus, Video, type LucideIcon } from 'lucide-react';
import type { HomeFirm, HomeResponse, NeedsYouItem, NeedsYouKind } from '@upm/domain';
import { api, ApiError } from '../lib/api';
import { firstName, fmtAgo, fmtDow, fmtTime, fmtTimeRange, greeting, plural, zoneShort } from '../lib/format';
import { Avatar, Banner, Button, ButtonA, ButtonLink, Pill, Spinner, cx, initialsOf } from '../ui';
import { useMe } from './AdminShell';
import { InviteDialog } from './Pipeline';

const KIND: Record<NeedsYouKind, { icon: LucideIcon; label: string; action: string; hash?: string }> = {
  unread_message: { icon: MessageSquare, label: 'New message', action: 'Reply', hash: '#messages' },
  contact_requested: { icon: CalendarClock, label: 'Discovery Session time needed', action: 'Find a time', hash: '#messages' },
  link_requested: { icon: Link2, label: 'Link requested', action: 'Reissue link' },
  file_rejected: { icon: FileWarning, label: 'File rejected', action: 'Follow up', hash: '#messages' },
  review_submission: { icon: ClipboardCheck, label: 'Ready for review', action: 'Review' },
  task_overdue: { icon: ListTodo, label: 'Task overdue', action: 'Open gates', hash: '#gates' },
  missing_files: { icon: FileClock, label: 'Files missing', action: 'Send email', hash: '#files' },
  stalled: { icon: Hourglass, label: 'Stalled', action: 'Check in', hash: '#messages' },
};

const reviewPath = (id: string, hash = '') => `/admin/onboarding/${id}${hash}`;

export function HomePage() {
  const { me, reportNeedsYou } = useMe();
  const [data, setData] = useState<HomeResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    try { const h = await api.get<HomeResponse>('/api/admin/home'); setData(h); setError(null); reportNeedsYou(h.counts.needsYou); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not load Home.'); }
  }, [reportNeedsYou]);
  useEffect(() => { void load(); }, [load]);

  const name = firstName(me?.name || me?.email?.replace(/@.*/, ''), '');
  const today = new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric' }).format(new Date());
  const setupOpen = data && (!data.setup.outlookConnected || !data.setup.hasInvitedFirm);

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
          <h1 className="page-title">{greeting()}{name ? `, ${name}` : ''}</h1>
          <span className="text-[13px] text-muted">{today}</span>
        </div>
        <Button variant="primary" size="sm" onClick={() => setInviting(true)}>
          <Plus aria-hidden="true" size={16} strokeWidth={2} />Invite a firm
        </Button>
      </div>
      {error && <Banner tone="error" role="alert">{error}</Banner>}
      {!data && !error && <Spinner label="Loading Home" />}
      {data && (
        <div className="flex flex-col gap-4">
          {setupOpen && <SetupChecklist setup={data.setup} onInvite={() => setInviting(true)} />}
          <div className="grid grid-cols-1 items-start gap-6 lg:grid-cols-3">
            <NeedsYou items={data.needsYou} className="lg:col-span-2" />
            <Kickoffs items={data.upcomingKickoffs} />
          </div>
          <AllFirms firms={data.firms} />
          <Activity items={data.activity} />
        </div>
      )}
      <InviteDialog open={inviting} onClose={() => setInviting(false)} onCreated={load} />
    </>
  );
}

// ---------------------------------------------------------------- shared card chrome

function Section({ id, title, count, countTone = 'neutral', action, children, className }: {
  id: string; title: string; count?: number; countTone?: 'neutral' | 'warn'; action?: ReactNode; children: ReactNode; className?: string;
}) {
  return (
    <section aria-labelledby={id} className={cx('min-w-0 overflow-hidden rounded-2xl border border-line bg-white shadow-card', className)}>
      <div className="flex min-h-[44px] items-center justify-between gap-3 border-b border-line-soft px-4 py-2 sm:px-5">
        <h2 id={id} className="card-title flex items-center gap-2.5">
          {title}
          {count !== undefined && (
            <span className={cx('rounded-pill px-2 py-px text-[13px] font-semibold tabular-nums', countTone === 'warn' && count > 0 ? 'bg-warn-bg text-warn-fg' : 'bg-subtle text-ink-2')}>
              {count}<span className="sr-only"> items</span>
            </span>
          )}
        </h2>
        {action}
      </div>
      {children}
    </section>
  );
}

function Empty({ icon: Icon, title, children }: { icon: LucideIcon; title: string; children?: ReactNode }) {
  return (
    <div className="flex items-center gap-3.5 px-4 py-4 sm:px-5">
      <span aria-hidden="true" className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-ok-bg text-ok"><Icon size={20} strokeWidth={2} /></span>
      <div className="min-w-0">
        <div className="font-semibold">{title}</div>
        {children && <div className="text-sm text-muted">{children}</div>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- setup

function SetupChecklist({ setup, onInvite }: { setup: HomeResponse['setup']; onInvite: () => void }) {
  const steps = [
    { done: setup.outlookConnected, title: 'Connect Outlook', detail: 'Invites and firm emails go out from your own mailbox instead of no-reply@, and Discovery Sessions land on your calendar.',
      action: <ButtonLink to="/admin/settings" variant="outline" size="sm">Connect Outlook</ButtonLink> },
    { done: setup.hasInvitedFirm, title: 'Invite your first firm', detail: 'They get a secure link to the onboarding questionnaire. No account needed.',
      action: <Button variant="outline" size="sm" onClick={onInvite}>Invite a firm</Button> },
  ];
  const done = steps.filter((s) => s.done).length;
  return (
    <Section id="setup-h" title="Finish setting up" action={<span className="text-[13px] font-semibold text-muted tabular-nums">{done} of {steps.length} done</span>}>
      <ol className="m-0 list-none p-0">
        {steps.map((s) => (
          <li key={s.title} className="flex flex-wrap items-start gap-x-4 gap-y-2 border-t border-line-soft px-4 py-2.5 first:border-t-0 sm:px-5">
            {s.done ? (
              <span aria-hidden="true" className="flex h-6 w-6 flex-none items-center justify-center rounded-full bg-brand"><Check size={14} strokeWidth={3} className="text-white" /></span>
            ) : <span aria-hidden="true" className="box-border h-6 w-6 flex-none rounded-full border-[1.5px] border-line-btn" />}
            <div className="min-w-0 flex-[1_1_240px]">
              <div className={cx('font-semibold', s.done && 'font-medium text-muted')}>{s.title}<span className="sr-only">{s.done ? ' (done)' : ' (to do)'}</span></div>
              {!s.done && <div className="text-sm text-muted">{s.detail}</div>}
            </div>
            {!s.done && <div className="ml-10 sm:ml-0">{s.action}</div>}
          </li>
        ))}
      </ol>
    </Section>
  );
}

// ---------------------------------------------------------------- needs you

function NeedsYou({ items, className }: { items: NeedsYouItem[]; className?: string }) {
  return (
    <Section id="needs-h" title="Needs you" count={items.length} countTone="warn" className={className}>
      {items.length === 0 ? (
        <Empty icon={CircleCheck} title="You’re all caught up.">New messages, submissions and requests from firms show up here.</Empty>
      ) : (
        <ul className="m-0 list-none p-0">
          {items.map((n, i) => <NeedsYouRow key={`${n.kind}-${n.onboardingId}-${i}`} n={n} />)}
        </ul>
      )}
    </Section>
  );
}

function NeedsYouRow({ n }: { n: NeedsYouItem }) {
  const k = KIND[n.kind];
  const navigate = useNavigate();
  const to = reviewPath(n.onboardingId, k.hash);
  return (
    <li
      className="group flex cursor-pointer items-center gap-3 border-t border-line-soft px-4 py-2 first:border-t-0 hover:bg-row-hi sm:px-5"
      onClick={() => navigate(to)}
    >
      <span aria-hidden="true" className="flex h-7 w-7 flex-none items-center justify-center rounded-lg bg-brand-soft text-brand">
        <k.icon size={15} strokeWidth={1.9} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
          <Link to={to} className="font-semibold text-ink no-underline hover:underline" onClick={(e) => e.stopPropagation()}>{n.firmName}</Link>
          <span className="text-[13px] text-muted">{k.label} · <time dateTime={n.at} className="tabular-nums">{fmtAgo(n.at)}</time></span>
        </div>
        <div className="line-clamp-2 text-sm text-ink-2 sm:line-clamp-1" title={n.summary}>{n.summary}</div>
      </div>
      <ButtonLink to={to} variant="secondary" size="sm" className="hidden flex-none sm:inline-flex" onClick={(e) => e.stopPropagation()}>
        {k.action}<span className="sr-only">: {n.firmName}</span>
      </ButtonLink>
      <ArrowRight aria-hidden="true" size={18} className="flex-none text-line-strong sm:hidden" />
    </li>
  );
}

// ---------------------------------------------------------------- Discovery Sessions

function Kickoffs({ items }: { items: HomeResponse['upcomingKickoffs'] }) {
  return (
    <Section id="kick-h" title="Discovery Sessions this week" count={items.length}>
      {items.length === 0 ? (
        <p className="m-0 px-4 py-4 text-sm text-muted sm:px-5">No Discovery Sessions booked in the next 7 days.</p>
      ) : (
        <ul className="m-0 list-none p-0">
          {items.map((k) => {
            const d = new Date(k.startsAt);
            return (
              <li key={k.onboardingId + k.startsAt} className="flex gap-3 border-t border-line-soft px-4 py-2.5 first:border-t-0 sm:px-5">
                <div aria-hidden="true" className="flex h-10 w-10 flex-none flex-col items-center justify-center rounded-lg border border-line leading-none">
                  <span className="text-[11px] font-bold uppercase tracking-[0.06em] text-muted">{fmtDow(d)}</span>
                  <span className="mt-0.5 text-base font-bold tabular-nums">{d.getDate()}</span>
                </div>
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <div className="min-w-0">
                    <Link to={reviewPath(k.onboardingId)} className="font-semibold text-ink no-underline hover:underline">{k.firmName}</Link>
                    <div className="text-sm tabular-nums text-muted">
                      <span className="sr-only">{d.toDateString()}, </span>{fmtTimeRange(k.startsAt, k.endsAt)} {zoneShort(d)}
                    </div>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {/* Files are requested after the session: no pill until they are. */}
                    {k.filesRequested && (k.missingFiles > 0
                      ? <Pill tone="warn" className="!py-0.5 !text-xs">{plural(k.missingFiles, 'file')} missing</Pill>
                      : <Pill tone="ok" className="!py-0.5 !text-xs">Files in</Pill>)}
                    {k.joinUrl && (
                      <ButtonA href={k.joinUrl} target="_blank" rel="noreferrer" variant="link" size="sm" className="!min-h-7">
                        <Video aria-hidden="true" size={15} />Join<span className="sr-only"> the call with {k.firmName}</span>
                      </ButtonA>
                    )}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </Section>
  );
}

// ---------------------------------------------------------------- all firms

const STAGE_TITLES = ['Plan', 'Prepare', 'Launch', 'Succeed'];

/** Four-segment journey bar: each segment fills by steps done in that stage. */
export function JourneyBar({ stages, firmName }: { stages: HomeFirm['stages']; firmName: string }) {
  const current = stages.find((s) => s.state === 'in_progress');
  const label = `${firmName}: ${current ? `${current.title}, ${current.done} of ${current.total} steps done` : 'all stages done'}`;
  return (
    <div role="img" aria-label={label} className="grid grid-cols-4 gap-1">
      {stages.map((s) => (
        <span key={s.id} className="block h-1.5 overflow-hidden rounded-full bg-line">
          <span className="block h-full rounded-full bg-brand" style={{ width: `${s.total ? Math.round((s.done / s.total) * 100) : 0}%` }} />
        </span>
      ))}
    </div>
  );
}

function NextAction({ f }: { f: HomeFirm }) {
  if (f.nextAt) {
    const d = new Date(f.nextAt);
    return <span className="font-semibold text-ink">{f.nextAction} <span className="tabular-nums">{fmtDow(d)} {fmtTime(d)}</span></span>;
  }
  if (f.waitingOn === 'firm') return <span className="text-muted">{f.nextAction}</span>;
  return <span className="font-semibold text-brand">{f.nextAction}</span>;
}

function AllFirms({ firms }: { firms: HomeFirm[] }) {
  return (
    <Section
      id="firms-h"
      title="All firms"
      count={firms.length}
      action={<Link to="/admin/onboarding" className="text-sm font-semibold no-underline hover:underline">Open pipeline</Link>}
    >
      {firms.length === 0 ? (
        <p className="m-0 px-4 py-4 text-sm text-muted sm:px-5">No active firms. Invited firms show their progress here.</p>
      ) : (
        <>
          <div aria-hidden="true" className="hidden grid-cols-[minmax(0,1.3fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_96px] gap-6 border-b border-line-soft px-5 py-2 text-xs font-bold uppercase tracking-[0.06em] text-muted md:grid">
            <span>Firm</span>
            <span className="grid grid-cols-4 gap-1">{STAGE_TITLES.map((t) => <span key={t}>{t}</span>)}</span>
            <span>Next</span>
            <span className="text-right">Activity</span>
          </div>
          <ul className="m-0 list-none p-0">
            {firms.map((f) => <FirmRow key={f.onboardingId} f={f} />)}
          </ul>
        </>
      )}
    </Section>
  );
}

function FirmRow({ f }: { f: HomeFirm }) {
  const navigate = useNavigate();
  const to = reviewPath(f.onboardingId);
  return (
    <li
      className="grid cursor-pointer grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 border-t border-line-soft px-4 py-2 first:border-t-0 hover:bg-row-hi md:grid-cols-[minmax(0,1.3fr)_minmax(0,1.4fr)_minmax(0,1.2fr)_96px] md:gap-x-6 md:px-5"
      onClick={() => navigate(to)}
    >
      <div className="flex min-w-0 items-center gap-3">
        <Avatar initials={initialsOf(f.firmName, 'firm')} size={28} tone="soft" square />
        <div className="flex min-w-0 flex-col">
          <Link to={to} className="truncate font-semibold text-ink no-underline hover:underline" onClick={(e) => e.stopPropagation()}>{f.firmName}</Link>
          <span className="truncate text-[13px] text-muted">{f.stageLabel}</span>
        </div>
      </div>
      <span className="text-right text-[13px] tabular-nums text-muted md:order-last">{fmtAgo(f.lastActivityAt)}</span>
      <div className="col-span-2 md:col-span-1"><JourneyBar stages={f.stages} firmName={f.firmName} /></div>
      <div className="col-span-2 min-w-0 truncate text-sm md:col-span-1"><NextAction f={f} /></div>
    </li>
  );
}

// ---------------------------------------------------------------- activity

const ACTIVITY_SHOWN = 8;

function Activity({ items }: { items: HomeResponse['activity'] }) {
  const [all, setAll] = useState(false);
  const shown = all ? items : items.slice(0, ACTIVITY_SHOWN);
  return (
    <Section
      id="act-h"
      title="Recent activity"
      action={items.length > ACTIVITY_SHOWN ? (
        <Button variant="link" size="sm" aria-expanded={all} onClick={() => setAll((x) => !x)}>{all ? 'Show less' : `Show all ${items.length}`}</Button>
      ) : undefined}
    >
      {items.length === 0 ? (
        <p className="m-0 px-4 py-4 text-sm text-muted sm:px-5">Nothing yet. Opens, saves, uploads and messages from firms appear here.</p>
      ) : (
        <ol className="m-0 list-none p-0">
          {shown.map((a, i) => (
            <li key={`${a.at}-${i}`} className="flex items-baseline gap-4 border-t border-line-soft px-4 py-1.5 first:border-t-0 sm:px-5">
              <span className="min-w-0 flex-1 text-sm">
                <Link to={reviewPath(a.onboardingId)} className="font-semibold text-ink no-underline hover:underline">{a.firmName}</Link>
                <span className="text-ink-2"> {lowerFirst(a.summary)}</span>
              </span>
              <time dateTime={a.at} className="flex-none text-[13px] tabular-nums text-muted">{fmtAgo(a.at)}</time>
            </li>
          ))}
        </ol>
      )}
    </Section>
  );
}

/** "Submitted the questionnaire" reads as "Bluebird submitted the questionnaire". */
const lowerFirst = (s: string) => s.charAt(0).toLowerCase() + s.slice(1);
